import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { User, Package, LogOut, Settings, ShieldCheck, ClipboardList, FileText } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

export default function AccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isStaff } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto text-center">
            <User className="h-16 w-16 text-muted-foreground/50 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold mb-4">{t('nav.account')}</h1>
            <p className="text-muted-foreground mb-8">
              Connectez-vous pour accéder à votre compte et suivre vos commandes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link to="/login">{t('auth.login')}</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/login?register=true">{t('auth.register')}</Link>
              </Button>
            </div>
          </div>
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-display text-4xl font-bold mb-2">{t('nav.account')}</h1>
            <p className="text-muted-foreground mb-8">
              Bienvenue, {user.first_name || user.email}
            </p>

            <div className="space-y-4">
              {/* Profile */}
              <div className="border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link 
                  to="/account/orders"
                  className="border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors"
                >
                  <Package className="h-6 w-6 mb-3" />
                  <h3 className="font-medium">Mes commandes</h3>
                  <p className="text-sm text-muted-foreground">
                    Suivez vos commandes et consultez l'historique
                  </p>
                </Link>

                <Link 
                  to="/account/messages"
                  className="border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors"
                >
                  <ClipboardList className="h-6 w-6 mb-3" />
                  <h3 className="font-medium">Mes messages</h3>
                  <p className="text-sm text-muted-foreground">
                    Échangez avec un conseiller ou le support
                  </p>
                </Link>

                <Link 
                  to="/account/settings"
                  className="border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors"
                >
                  <Settings className="h-6 w-6 mb-3" />
                  <h3 className="font-medium">Paramètres</h3>
                  <p className="text-sm text-muted-foreground">
                    Gérez vos informations personnelles
                  </p>
                </Link>

                <Link 
                  to="/account/quiz-results"
                  className="border border-border rounded-lg p-6 hover:bg-muted/50 transition-colors"
                >
                  <FileText className="h-6 w-6 mb-3" />
                  <h3 className="font-medium">Mes résultats de quiz</h3>
                  <p className="text-sm text-muted-foreground">
                    Consultez vos profils style
                  </p>
                </Link>
              </div>

              {/* Staff/Admin Link */}
              {isStaff() && (
                <Link 
                  to="/admin"
                  className="border-2 border-primary rounded-lg p-6 hover:bg-primary/5 transition-colors flex items-center gap-4"
                  data-testid="admin-panel-link"
                >
                  <ShieldCheck className="h-8 w-8" />
                  <div>
                    <h3 className="font-medium text-lg">
                      {user?.role === 'SUPPORT' ? 'Espace Support' : user?.role === 'ADVISOR' ? 'Espace Conseiller' : 'Accès Admin'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {user?.role === 'SUPPORT' 
                        ? 'Gérez les conversations clients'
                        : user?.role === 'ADVISOR'
                        ? 'Conseillez vos clients et gérez vos ventes'
                        : 'Gérez la boutique, les commandes et les produits'
                      }
                    </p>
                  </div>
                </Link>
              )}

              {/* Logout */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('auth.logout')}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </StoreLayout>
  );
}
